// Modules
import { createAction, type Dispatch } from '@reduxjs/toolkit';
import { saveToLocalStorage, IMAGE_SEARCH_FILTERS_MAPPED } from '@/lib/helpers';
import { api } from '@/lib/trpc/client';
import type { ImageSearchItemResponse } from '@/server/api/routers/image-search';
import type { RootState } from '@/app/store';

type ErrorObject = {
  message?: string;
  data?: {
    code?: string;
  };
};

interface ImageSearchFilterValue {
  brand: string;
  targetGroup: string;
  season: string;
  sport: string;
  imgType: string;
  imgWidth: string;
  imgHeight: string;
  sapId: string;
  kwNumber: string;
  productName: string;
  assortment?: string;
  campaign?: string;
  category?: string;
  delivery?: string;
  productgroup?: string;
  productLabels?: string;
  productType?: string;
  mainSport?: string;
  subcategory?: string;
  targetgroup?: string;
  technologies?: string;
  warranty?: string;
  warehouse?: string;
  b2bBlocked?: boolean;
  deliverySalesProduct?: boolean;
  priceType?: string;
  wholesaleProduct?: boolean;
}

interface SearchFilterMappingValue {
  operator: string;
}

type ImageSearchFiltersMappedType = Record<string, SearchFilterMappingValue>;

// --- Helper Functions ---
function getTrcpErrorMessage(error: ErrorObject): string {
  return String(error?.message || error?.data?.code || 'Unknown error');
}

// --- Action Creators ---
export const setImageSearchActiveOperator = createAction<string>(
  'image-search/active-operator/set'
);

export const setImageSearchActiveFilters = createAction<ImageSearchFilterValue>(
  'image-search/active-filters/save'
);

export const _setImageSearchActiveFilters = (activeFilters: any) => (dispatch: Dispatch) => {
  let filtersToSave: ImageSearchFilterValue;

  if (activeFilters === null) {
    filtersToSave = {
      brand: '',
      targetGroup: '',
      season: '',
      sport: '',
      imgType: '',
      imgWidth: '',
      imgHeight: '',
      sapId: '',
      kwNumber: '',
      productName: '',
      assortment: '',
      campaign: '',
      category: '',
      delivery: '',
      productgroup: '',
      productLabels: '',
      productType: '',
      mainSport: '',
      subcategory: '',
      targetgroup: '',
      technologies: '',
      warranty: '',
      warehouse: '',
      b2bBlocked: false,
      deliverySalesProduct: false,
      priceType: '',
      wholesaleProduct: false,
    };
  } else {
    filtersToSave = activeFilters;
  }
  saveToLocalStorage('imageSearchActiveFilters', filtersToSave);
  dispatch(setImageSearchActiveFilters(filtersToSave) as any);
};

export const imageSearchRequestStarted = createAction('image-search/request/started');
export const imageSearchRequestFailed = createAction<string>('image-search/request/failed');

export const fetchImagesSucceeded = createAction<ImageSearchItemResponse[]>(
  'image-search/fetch/succeeded'
);

export const downloadImagesRequestStarted = createAction('image-download/request/started');
export const downloadImagesRequestFailed = createAction<string>('image-download/request/failed');

export const fetchImages = (dispatch: Dispatch, getState: () => RootState) => {
  dispatch(imageSearchRequestStarted());

  const state = getState();
  const { user, activeChain } = state.user;
  const { activeFilters } = state.imageSearch;

  const hasActiveFilters = Object.values(activeFilters).some(
    (val) =>
      !!val &&
      ((typeof val === 'string' && val.length > 0) ||
        (Array.isArray(val) && val.length > 0) ||
        (typeof val === 'boolean' && val === true))
  );

  if (!hasActiveFilters) {
    dispatch(fetchImagesSucceeded([]));
    return Promise.resolve();
  }

  const typedImageSearchFiltersMapped: ImageSearchFiltersMappedType =
    IMAGE_SEARCH_FILTERS_MAPPED as ImageSearchFiltersMappedType;

  interface ImageSearchFilterValue {
    brand?: string;
    season?: string[];
    productName?: string;
  }

  interface SearchFilterMappingValue {
    name: string;
    operator: string;
    type: string;
  }

  type TypedImageSearchFiltersMapped = Record<string, SearchFilterMappingValue>;

  interface FilterParamValue {
    operator: string;
    value: string | string[] | boolean;
  }

  const requestParams: Record<string, FilterParamValue> = Object.keys(activeFilters).reduce(
    (acc: Record<string, FilterParamValue>, key: string) => {
      if (key in typedImageSearchFiltersMapped) {
        const searchFilter =
          typedImageSearchFiltersMapped[key as keyof TypedImageSearchFiltersMapped];

        const filterValue = activeFilters[key as keyof ImageSearchFilterValue];

        if (
          filterValue !== undefined &&
          filterValue !== null &&
          ((typeof filterValue === 'string' && filterValue.length > 0) ||
            (Array.isArray(filterValue) && filterValue.length > 0) ||
            (typeof filterValue === 'boolean' && filterValue === true))
        ) {
          acc[key] = {
            operator: searchFilter?.operator ?? '',
            value: filterValue,
          };
        }
      }
      return acc;
    },
    {} as Record<string, FilterParamValue>
  );

  return api.imageSearch.searchImages
    .query({
      chain: activeChain,
      filters: requestParams,
    })
    .then(
      (images) => {
        dispatch(fetchImagesSucceeded(images));
      },
      (error: ErrorObject) => {
        dispatch(imageSearchRequestFailed(getTrcpErrorMessage(error)));
      }
    );
};

export const downloadImages =
  (studioIds: string, sapId: string | undefined) => (dispatch: Dispatch) => {
    dispatch(downloadImagesRequestStarted());

    const queryParams = {
      studioIds,
      sapId,
    };

    return api.imageSearch.getDownloadUrl.query(queryParams).then(
      (zipUrl) => window.open(zipUrl, '_blank'),
      (error: ErrorObject) => dispatch(downloadImagesRequestFailed(getTrcpErrorMessage(error)))
    );
  };
